import React, { useContext, useId, useMemo, useState } from "react";
import { groupBy, sleep, sortMapByKey } from "simple-utils";
import { Modal, useModalHide } from "../../components/Modal";
import { getFranchise, getFranchiseById } from "../../models/Account";
import { FullReportEntry } from "../../models/FullReportEntry";
import { getRegion, stateName } from "../../utils/state-utils";
import { useShortcut } from "../../utils/utils";
import { AccountContext } from "../AccountContext";
import { CategoryFilterMenu } from "./category-filters";
import { ClusterFilterMenu } from "./cluster-filters";
import { isEmptyFilter } from "./filters";
import { FilterAction } from './FilterAction';
import { TagContext } from "../../tags/tag-context";
import { TagFilter } from "../../tags/tag-filter";

interface MenuData{
    id: string
    label: string
}

function getMenuData(accounts:FullReportEntry[], getId:(a:FullReportEntry)=>string, getLabel:(id:string)=>string, placeholder = "Selecione..."):MenuData[]{
    var groups = groupBy(accounts, a => getId(a));
    var values = Object.keys(groups).map(k => ({id: k, label: getLabel(k)}));
    values.sort((a, b) => a.label.localeCompare(b.label));
    return [{id:"", label: placeholder}].concat(values);
}

function getMenuData2(accounts:FullReportEntry[], field:keyof FullReportEntry, placeholder = "Selecione..."):MenuData[]{
    var items = accounts.map(a => {
        const k =  a[field] as string; 
        if(!k) return null;
        return k.split(/ *, */g)
    }).filter(a => a != null).flat()
    var groups = groupBy(items, a => a)
    var values = Object.keys(groups).map(k => ({id: k, label:`${k} (${groups[k].length})`}));
    values.sort((a, b) => a.label.localeCompare(b.label));
    return [{id:"", label: placeholder}].concat(values);
}


function buildAllMenus(accounts:FullReportEntry[]){
    return {
        ufMenu: getMenuData(accounts, a => a.state, s => stateName(s), "Estado..."),
        regionMenu: getMenuData(accounts, a =>  getRegion(a.state), s => s, "Região..."),
        franchiseMenu: getMenuData(accounts, a => a.franchise + "", a =>  getFranchiseById(a), "Franquia..."),
        groupMenu: getMenuData2(accounts, "group", "Grupo..."),
        consultantMenu: getMenuData2(accounts, "consultant", "Consultor..."),
        managerMenu: getMenuData2(accounts, "manager", "Gestor..."),
        goalMenu: [{id:"", label: "Meta..."}, {id:"Acima", label: "Meta Acima"}, {id:"Abaixo", label: "Meta Abaixo"}, {id:"Muito abaixo", label: "Meta Muito Abaixo"}, {id:"Abaixo Geral", label: "Meta Abaixo Geral"} ],
        overrunMenu: [{id:"", label: "Problema no orçamento..."}, {id:"Alto", label: "Investimento Alto"}, {id:"Baixo", label: "Investimento Baixo"}, {id:"Alto ou Baixo", label: "Investimento Alto ou Baixo"}, {id:"Neutro", label: "Investimento Ok"}]
    }
}


function Menu({label, selected, values, onChangeAction}:{label:string, selected:string, values:MenuData[], onChangeAction: FilterAction}){
    const {dispatch} = useContext(AccountContext);

    function doChange(newValue:string){
        dispatch({type: onChangeAction, payload: newValue});
    }

    return <div className="menu my-2">
        <select className="form-select" value={selected || ""} onChange={(e) => doChange(e.target.value)}>
            {values.map(o => <option value={o.id}>{o.label}</option> )}
        </select>
    </div>
}

function GroupedMenu({label, selected, values, onChangeAction, groupFunction}:{label:string, selected:string, 
            values:MenuData[], onChangeAction: FilterAction, 
            groupFunction:(m:MenuData)=>string}){
    const {dispatch} = useContext(AccountContext);

    function doChange(newValue:string){
        dispatch({type: onChangeAction, payload: newValue});
    }

    const groups = sortMapByKey(groupBy(values.filter(s => s.id != ""), groupFunction), (a, b) => a.localeCompare(b));


    return <div className="menu my-2">
        <select className="form-select" value={selected || ""} onChange={(e) => doChange(e.target.value)}>
            <option value={""}>{values[0].label}</option>
            {Object.entries(groups).map(([k, v]) => 
               <optgroup label={k}>
                     {v.map(o => <option value={o.id}>{o.label}</option> )}
               </optgroup>) }
        </select>
    </div>
}

export function ToggleFilter({label, on, toggleAction}:{label:string, on:boolean, toggleAction:FilterAction | "setShowPrevious"}){
    const {dispatch} = useContext(AccountContext);

    function toggle(){
        dispatch({type: toggleAction, payload: !on})
    }

    var id = useId();

    return <div className="form-check my-3">
        <input className="form-check-input" type="checkbox" checked={on || false} onChange={toggle} id={id} />
        <label className="form-check-label" htmlFor={id}>
            {label}
        </label>
    </div>
}


function FilterModal(){
    const {filters, accounts, dispatch} = useContext(AccountContext);
    const {state:{tags}} = useContext(TagContext);

    const menus = useMemo(() => buildAllMenus(accounts), [accounts]);
    const tagMenu = useMemo(() => {
        const all = Object.values(tags).reduce((list, t) => [...list, ...t], []);
        const byName = groupBy(all, t=>t.name);
        return Object.values(byName).map(t => t[0]);
    }, [tags]);
    const hide = useModalHide();

    async function clearFilters(){
        await hide();
        dispatch({type: FilterAction.clearFilters})
    }

    function onUpdateTags(selected:number[]){
        if(selected.length == 0)
            return dispatch({type:FilterAction.setTagFilter, payload: null});
        const all = Object.values(tags).reduce((list, t) => [...list, ...t], []);
        const tagsSelected = all.filter(a => selected.includes(a.id));
        const labelsSelected = tagsSelected.map(a=>a.name)
        return dispatch({type:FilterAction.setTagFilter, payload: all.filter(a => labelsSelected.includes(a.name)).map(a => a.id)});
    }

    return <>
        <Menu label="Franquia" values={menus.franchiseMenu} selected={filters?.franchise} onChangeAction={FilterAction.setFranchise} /> 
        <GroupedMenu groupFunction={s => getRegion(s.id)} label="Estado" values={menus.ufMenu} selected={filters?.uf} onChangeAction={FilterAction.setUf} />   
        <Menu label="Região" values={menus.regionMenu} selected={filters?.region} onChangeAction={FilterAction.setRegion} /> 
        <Menu label="Grupo" values={menus.groupMenu} selected={filters?.group} onChangeAction={FilterAction.setGroup} /> 
        <Menu label="Gestor" values={menus.managerMenu} selected={filters?.manager} onChangeAction={FilterAction.setManager} /> 
        <Menu label="Consultor" values={menus.consultantMenu} selected={filters?.consultant} onChangeAction={FilterAction.setConsultant} /> 
        <Menu label="Meta" values={menus.goalMenu} selected={filters?.goal} onChangeAction={FilterAction.setGoal} /> 
        <Menu label="Overrun" values={menus.overrunMenu} selected={filters?.overrun} onChangeAction={FilterAction.setOverrun} /> 
        <CategoryFilterMenu selected={filters?.category || ""} /> 
        <ClusterFilterMenu selected={filters?.cluster || ""} />
        <TagFilter tags={tagMenu} selected={filters?.tags || []} onSelect={onUpdateTags} /> 
        
        <ToggleFilter label="Status com problema" on={filters?.badStatus || false} toggleAction={FilterAction.badStatus} />  
        <ToggleFilter label="Mudou de status Hoje" on={filters?.todayStatus || false} toggleAction={FilterAction.todayStatus} />  
        <ToggleFilter label="Sem informações hoje" on={filters?.noInfo || false} toggleAction={FilterAction.noInfo} />  
        <ToggleFilter label="Dias Faltando" on={filters?.missingDays || false} toggleAction={FilterAction.missingDays} />  
        <ToggleFilter label="Alerta de saldo" on={filters?.badBalance || false} toggleAction={FilterAction.badBalance} /> 
        <ToggleFilter label="Menos leads que mês passado" on={filters?.worseThanPastMonth || false} toggleAction={FilterAction.worseThanPastMonth} /> 
        
        <p className="text-center mt-4">
            <button className="btn btn-secondary me-2" onClick={clearFilters}>Limpar Filtros</button>
            <button className="btn btn-primary" onClick={() => hide()}>Fechar</button>
        </p>
    </>
}

export function FilteringButton(){
    const [show, setShow] = useState(false);
    const {filters} = useContext(AccountContext)

    useShortcut("KeyF", () => setShow(true));

    return <>
        <button onClick={(e) => {setShow(true); e.currentTarget.blur();}} className={`ms-0 btn btn-link text-center px-0 ${isEmptyFilter(filters) ? '' : 'active-filter'}`} style={{width: "34px"}}><i className="fa fa-sliders fa-rotate-90"></i></button>
        {show && <Modal className="modal-primary" onHide={() => setShow(false)} title="Filtros"><FilterModal /></Modal>}
    </>
}
